<template>
  <div class="room-wrapper" :style="roomWrapperStyle">
    <!-- ══════════════════════════════════════════════════
         CONNECTING overlay
    ═══════════════════════════════════════════════════ -->
    <div v-if="!connected" class="connecting-overlay">
      <v-progress-circular indeterminate color="#A6CC39" :size="60" :width="5" />
      <p class="mt-4" style="color: #fff; font-size: 18px">Connecting to live session…</p>
    </div>

    <!-- ══════════════════════════════════════════════════
         MAIN LAYOUT (once connected)
    ═══════════════════════════════════════════════════ -->
    <div v-show="connected" style="display:flex;flex:1;overflow:hidden">
      <!-- ── Main stage ─────────────────────────────── -->
      <div class="main-stage">
        <!-- Screen share view (when host is sharing screen) -->
        <div v-if="isScreenSharing" class="screen-share-layout">
          <div class="screen-share-main">
            <video ref="screenShareVideo" autoplay playsinline class="main-video"></video>
            <!-- Drawing overlay — mirrors host's canvas strokes -->
            <canvas ref="drawCanvas" class="draw-overlay"></canvas>
          </div>
          <!-- Camera filmstrip (all remote cameras + local) -->
          <div class="camera-filmstrip">
            <div v-for="rp in remoteVideoTracks" :key="rp.sid" class="filmstrip-item">
              <video :ref="'rv_' + rp.sid" autoplay playsinline class="filmstrip-video"></video>
              <span class="filmstrip-label">{{ rp.name }}</span>
            </div>
            <!-- Local camera in filmstrip -->
            <div v-if="learnerMediaAllowed && !camOff" class="filmstrip-item filmstrip-item--local">
              <video ref="localVideoFilmstrip" autoplay playsinline muted class="filmstrip-video local-mirror"></video>
              <span class="filmstrip-label">You</span>
            </div>
          </div>
        </div>

        <!-- Grid view (no screen share — show all cameras in grid) -->
        <div v-else class="camera-grid-layout">
          <!-- No video placeholder -->
          <div v-if="remoteVideoTracks.length === 0 && camOff" class="no-video-placeholder">
            <v-icon size="80" color="#555">mdi-broadcast-off</v-icon>
            <p style="color: #888; margin-top: 12px">Waiting for host video…</p>
          </div>

          <!-- Remote participant cameras -->
          <div v-for="rp in remoteVideoTracks" :key="rp.sid" class="grid-video-item"
            :class="{ 'grid-video-item--single': remoteVideoTracks.length === 1 && camOff }">
            <video :ref="'rv_' + rp.sid" autoplay playsinline class="grid-video"></video>
            <span class="grid-video-label">{{ rp.name }}</span>
          </div>

          <!-- Local camera in grid -->
          <div v-if="learnerMediaAllowed && !camOff" class="grid-video-item grid-video-item--local">
            <video ref="localVideoGrid" autoplay playsinline muted class="grid-video local-mirror"></video>
            <span class="grid-video-label">You</span>
          </div>

          <!-- Drawing overlay — mirrors host's canvas strokes -->
          <canvas v-if="!isScreenSharing" ref="drawCanvas" class="draw-overlay"></canvas>
        </div>

        <!-- Local media controls — only when host allows learner media -->
        <template v-if="learnerMediaAllowed">
          <!-- Bottom media controls bar -->
          <div class="media-controls-bar">
            <v-btn fab small :color="micMuted ? '#E53935' : 'rgba(0,0,0,0.55)'" dark @click="toggleMic()" class="mx-2">
              <v-icon>{{ micMuted ? 'mdi-microphone-off' : 'mdi-microphone' }}</v-icon>
            </v-btn>
            <v-btn fab small :color="camOff ? '#E53935' : 'rgba(0,0,0,0.55)'" dark @click="toggleCam()" class="mx-2">
              <v-icon>{{ camOff ? 'mdi-camera-off' : 'mdi-camera' }}</v-icon>
            </v-btn>
          </div>
        </template>

        <!-- Media disabled notice — shown when host has not enabled learner media -->
        <div v-if="!learnerMediaAllowed" class="media-disabled-notice">
          <v-icon color="rgba(255,255,255,0.6)" size="16" class="mr-1">mdi-microphone-off</v-icon>
          <span>Camera &amp; mic disabled by host</span>
        </div>

        <!-- Controls bar (top-right) -->
        <div class="controls-bar">
          <!-- Screen share indicator -->
          <v-chip v-show="isScreenSharing" small color="#1976D2" dark class="mr-2">
            <v-icon small left>mdi-monitor-share</v-icon>
            Screen Share
          </v-chip>

          <!-- Breakout room join button (shown when assigned but modal was dismissed) -->
          <v-btn v-if="assignedBreakoutRoomID && !breakoutActive" small depressed color="#A6CC39" dark
            class="text-capitalize mr-2" style="border-radius: 8px" @click="startBreakoutInvitation()">
            <v-icon small left>mdi-account-group</v-icon>
            Join Breakout
          </v-btn>

          <!-- Participants count -->
          <v-chip small color="rgba(0,0,0,0.5)" dark class="mr-2">
            <v-icon small left>mdi-account-multiple</v-icon>
            {{ participantCount }}
          </v-chip>

          <!-- Leave button -->
          <v-btn small depressed color="#E53935" dark class="text-capitalize" style="border-radius: 8px"
            @click="leaveRoom()">
            <v-icon small left>mdi-phone-hangup</v-icon>
            Leave
          </v-btn>
        </div>
      </div>

      <!-- ── Chat sidebar ───────────────────────────── -->
      <div class="chat-sidebar">
        <!-- Tab Bar -->
        <div class="sidebar-tab-bar">
          <button class="sidebar-tab-btn" :class="{ active: selectedTab === 0 }" @click="selectTab(0)">
            <v-icon small class="mr-1">mdi-chat</v-icon>
            Chat
          </button>
          <button class="sidebar-tab-btn" :class="{ active: selectedTab === 1 }" @click="selectTab(1)">
            <v-icon small class="mr-1">mdi-account-group</v-icon>
            Participants
          </button>
          <button class="sidebar-tab-btn" :class="{ active: selectedTab === 2 }" @click="selectTab(2)">
            <v-icon small class="mr-1">mdi-help-circle-outline</v-icon>
            Q&amp;A
          </button>
          <button class="sidebar-tab-btn" :class="{ active: selectedTab === 3 }" @click="selectTab(3)">
            <v-icon small class="mr-1">mdi-poll</v-icon>
            Poll
          </button>
        </div>

        <!-- ═══ Chat Tab ═══ -->
        <template v-if="selectedTab === 0">
          <!-- Messages -->
          <div class="chat-messages" ref="chatScroll">
            <div v-if="chatMessages.length === 0" class="chat-empty">
              <p style="color: #a4a4a4; font-size: 13px; text-align: center; margin-top: 20px">
                No messages yet. Say hello!
              </p>
            </div>
            <div v-for="(msg, i) in chatMessages" :key="msg.id || i" class="chat-row mb-3 chat-bubble-wrap"
              :class="msg.mine ? 'chat-row--mine' : 'chat-row--theirs'">
              <!-- Body -->
              <div class="chat-row__body">
                <!-- Sender name — others only -->
                <div v-if="!msg.mine" class="chat-row__sender">{{ msg.sender }}</div>

                <!-- Bubble -->
                <div class="chat-row__group">
                  <div class="chat-row__bubble"
                    :class="msg.mine ? 'chat-row__bubble--mine' : 'chat-row__bubble--theirs'">
                    <!-- Reply quote block -->
                    <div v-if="msg.replyTo" class="chat-quote" :class="msg.mine ? 'chat-quote--mine' : ''">
                      <span class="chat-quote-name">{{ msg.replyTo.name }}</span>
                      <span class="chat-quote-text">{{ msg.replyTo.text }}</span>
                    </div>
                    {{ msg.text }}
                  </div>
                </div>

                <!-- Timestamp + reply button -->
                <div class="chat-row__foot">
                  <span class="chat-row__ts">{{ msg.time }}</span>
                  <v-btn text x-small class="chat-reply-btn" color="#6CC8A0" @click="startReply(msg)">Reply</v-btn>
                </div>
              </div>
            </div>
          </div>

          <!-- Emoji Reaction Bar -->
          <div class="emoji-bar">
            <button v-for="(emoji, index) in emojis" :key="index" class="emoji-btn"
              @click="sendEmoji(index + 1, emoji)">
              {{ emoji }}
            </button>
          </div>

          <!-- Floating Emoji Animations -->
          <div class="floating-emojis-container">
            <div v-for="fe in floatingEmojis" :key="fe.id" class="floating-emoji" :style="{ left: fe.startX + 'px' }"
              @animationend="removeFloatingEmoji(fe.id)">
              {{ fe.emoji }}
            </div>
          </div>

          <!-- Compose (reply banner + pill input) -->
          <div class="chat-compose">
            <div v-if="replyingToChatID" class="chat-reply-banner">
              <div class="chat-reply-banner__content">
                <v-icon size="14" color="#6CC8A0" class="mr-1">mdi-reply</v-icon>
                <span class="chat-reply-banner__name">Replying to {{ replyingToName }}</span>
              </div>
              <v-btn icon x-small @click="replyingToChatID = null; replyingToName = null; replyingToText = null">
                <v-icon size="16">mdi-close</v-icon>
              </v-btn>
            </div>
            <v-text-field ref="chatInput" v-model="chatMessage" append-icon="mdi-send" dense outlined rounded
              hide-details :disabled="sendingChat" placeholder="Type a message…" class="chat-input-pill"
              @click:append="sendChat" @keyup.enter="sendChat" />
          </div>
        </template>

        <!-- ═══ Poll Tab ═══ -->
        <div v-if="selectedTab === 3" class="poll-tab">
          <!-- Loading -->
          <div v-if="isPollLoading" class="poll-loading" data-testid="poll-loading">
            <v-progress-circular indeterminate color="#6cc8a0" :size="36" :width="3" />
          </div>

          <!-- No Active Poll -->
          <div v-else-if="!pollData" class="poll-empty" data-testid="poll-empty">
            <div class="poll-empty-icon">📊</div>
            <p>No active polls</p>
          </div>

          <!-- Vote Recorded (showResult = false, or B01: results could not be loaded — E20) -->
          <div v-else-if="hasVoted && (!pollData.showResult || pollResults.length === 0)"
            class="poll-voted-confirmation" data-testid="poll-voted-card">
            <div class="voted-card">
              <div class="voted-icon">✓</div>
              <h3>Your vote has been recorded!</h3>
              <p class="voted-question">{{ pollData.question }}</p>
            </div>
          </div>

          <!-- Results View (after voting OR timeout, when showResult = true) -->
          <div v-else-if="(hasVoted && pollData.showResult && pollResults.length > 0) ||
            (pollTimedOut && !hasVoted && pollData.showResult && pollResults.length > 0)" class="poll-results"
            data-testid="poll-results">
            <h3 class="poll-question">{{ pollData.question }}</h3>
            <div class="results-list">
              <div v-for="result in pollResults" :key="result.optionID" class="result-item"
                data-testid="poll-result-item">
                <div class="result-bar-container">
                  <!-- B01: thumb slot — keeps bars aligned when the poll has images -->
                  <div v-if="pollResultsHaveImages" class="result-thumb-slot">
                    <img v-if="hasPollImage(result)" :src="result.optionImage" alt="" class="result-thumb"
                      data-testid="poll-result-thumb" @error="markPollImageBroken(result.optionID)" />
                  </div>
                  <div class="result-bar-bg">
                    <div class="result-bar-fill" :style="{ width: getResultPercentage(result) + '%' }"></div>
                    <span class="result-option-text">{{ result.optionText }}</span>
                  </div>
                  <span class="result-percentage" data-testid="poll-result-percentage">
                    {{ getResultPercentage(result) }}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Timer Expired (showResult = false, or B01: results could not be loaded — UC14) -->
          <div v-else-if="pollTimedOut && !hasVoted && (!pollData.showResult || pollResults.length === 0)"
            class="poll-timeout" data-testid="poll-timeout-card">
            <div class="timeout-card">
              <div class="timeout-icon">⏰</div>
              <h3>Time's up!</h3>
              <p>You didn't answer the poll in time.</p>
              <p class="timeout-question">{{ pollData.question }}</p>
            </div>
          </div>

          <!-- Active Poll (Voting UI) -->
          <div v-else class="poll-active">
            <div class="poll-card">
              <!-- Header with timer -->
              <div class="poll-header">
                <span class="poll-header-icon">📊</span>
                <span class="poll-header-text">
                  Poll from Instructor — {{ formatTimer(pollTimerSeconds) }}
                </span>
              </div>

              <!-- Question -->
              <h3 class="poll-question">{{ pollData.question }}</h3>
              <p v-if="pollData.pollType === 2" class="poll-multi-hint">(Select multiple)</p>

              <!-- Options -->
              <div class="poll-options-grid">
                <!-- B01: option image (object-fit: contain, 72px) + text -->
                <button v-for="option in pollData.options" :key="option.optionID" class="poll-option-btn"
                  data-testid="poll-option" :class="{
                    selected: selectedPollOptions.includes(option.optionID),
                    disabled: pollTimerSeconds <= 0,
                    'poll-option-btn--with-image': hasPollImage(option)
                  }" :disabled="pollTimerSeconds <= 0" @click="togglePollOption(option.optionID)">
                  <img v-if="hasPollImage(option)" :src="option.optionImage" :alt="option.optionText"
                    class="poll-option-img" data-testid="poll-option-img" loading="lazy"
                    @error="markPollImageBroken(option.optionID)" />
                  <span class="poll-option-text">{{ option.optionText }}</span>
                </button>
              </div>

              <!-- Vote Button -->
              <button class="vote-submit-btn" data-testid="poll-vote-btn"
                :disabled="selectedPollOptions.length === 0 || pollTimerSeconds <= 0 || isVoting" @click="submitVote">
                <span v-if="isVoting" class="btn-spinner"></span>
                <span v-else>Vote</span>
              </button>
            </div>
          </div>
        </div>

        <!-- ═══ Participants Tab ═══ -->
        <div v-if="selectedTab === 1" class="extra-tab">
          <div v-if="participantLoading" class="extra-tab__empty">
            <v-progress-circular indeterminate color="#A6CC39" :size="32" :width="3" />
          </div>
          <div v-else-if="!participants.length" class="extra-tab__empty">
            <v-icon size="40" color="#ccc">mdi-account-group-outline</v-icon>
            <p>No participants yet</p>
          </div>
          <div v-else class="extra-tab__list">
            <div v-for="(p, i) in participants" :key="p.id || i" class="participant-row">
              <v-avatar :color="p.color" size="34" class="mr-3 flex-shrink-0">
                <span class="participant-initials">{{ p.initials }}</span>
              </v-avatar>
              <div class="participant-info">
                <div class="participant-name">{{ p.name }}</div>
                <div class="participant-role">{{ p.role }}</div>
              </div>
              <v-chip x-small label :color="p.badgeColor" :text-color="p.badgeText" class="ml-auto flex-shrink-0">
                {{ p.badge }}
              </v-chip>
            </div>
          </div>
        </div>

        <!-- ═══ Q&A Tab ═══ -->
        <!-- B02: สถานะ loading/error/empty/list (D28) + ช่องพิมพ์ pill แบบแชต (D25) + error ใต้ช่อง -->
        <div v-if="selectedTab === 2" class="extra-tab extra-tab--qa">
          <div v-if="qaLoading" class="extra-tab__empty" data-testid="qa-loading">
            <v-progress-circular indeterminate color="#A6CC39" :size="32" :width="3" />
          </div>
          <div v-else-if="qaLoadError && !questions.length" class="extra-tab__empty" data-testid="qa-load-error">
            <v-icon size="40" color="#E53935">mdi-alert-circle-outline</v-icon>
            <p>{{ qaLoadError }}</p>
          </div>
          <div v-else ref="qaList" class="extra-tab__list" data-testid="qa-list">
            <div v-if="qaLoadError" class="qa-list__error" data-testid="qa-load-error">{{ qaLoadError }}</div>
            <div v-if="!questions.length" class="extra-tab__empty" data-testid="qa-empty">
              <v-icon size="40" color="#ccc">mdi-help-circle-outline</v-icon>
              <p>No questions yet. Be the first!</p>
            </div>
            <div v-else>
              <div v-for="(q, i) in questions" :key="q.id || i" class="qa-item" data-testid="qa-item">
                <div class="qa-item__question" data-testid="qa-question">{{ q.question }}</div>
                <div class="qa-item__meta">{{ q.askedBy }} · {{ q.time }}</div>
                <v-chip x-small label :color="q.statusColor" :text-color="q.statusText" class="mt-1" data-testid="qa-status">{{ q.statusLabel }}</v-chip>
              </div>
            </div>
          </div>
          <div class="qa-compose" data-testid="qa-compose">
            <v-text-field v-model="newQuestion" append-icon="mdi-send" dense outlined rounded hide-details
              maxlength="500" :disabled="submittingQuestion" placeholder="Ask a question…" class="chat-input-pill"
              data-testid="qa-input" @input="qaError = ''" @click:append="submitQuestion()" @keyup.enter="submitQuestion()" />
            <div v-if="qaError" class="qa-compose__error" data-testid="qa-error">{{ qaError }}</div>
          </div>
        </div>


      </div>
    </div>

    <!-- ══════════════════════════════════════════════════
         BREAKOUT ROOM INVITATION MODAL
    ═══════════════════════════════════════════════════ -->
    <v-dialog v-model="showBreakoutModal" persistent max-width="400">
      <v-card style="border-radius:16px;overflow:hidden;">
        <div style="background:#A6CC39;padding:20px 24px 16px;">
          <div style="display:flex;align-items:center;gap:12px;">
            <v-icon color="white" size="32">mdi-account-group</v-icon>
            <div>
              <div style="color:#424242;font-size:18px;font-weight:700;line-height:1.2;">Breakout Room</div>
              <div style="color:rgba(255,255,255,0.85);font-size:13px;">You have been assigned to a breakout room</div>
            </div>
          </div>
        </div>
        <v-card-text style="padding:20px 24px 8px;">
          <p style="font-size:15px;color:#333;margin-bottom:8px;"><strong>{{ breakoutRoomLabel }}</strong></p>
          <p style="font-size:13px;color:#777;margin:0;">
            You will be moved automatically in <strong>{{ breakoutCountdown }}</strong> second{{ breakoutCountdown !== 1
              ?
              's' : '' }}.
          </p>
        </v-card-text>
        <v-card-actions style="padding:8px 16px 16px;">
          <v-spacer />
          <v-btn text color="#999" @click="dismissBreakout()">Stay</v-btn>
          <v-btn depressed color="#A6CC39" dark style="border-radius:8px;min-width:120px;"
            @click="joinBreakoutNow()">Join
            Now</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Breakout active banner -->
    <div v-if="breakoutActive" class="breakout-banner">
      <v-icon color="white" size="16" class="mr-1">mdi-account-group</v-icon>
      {{ breakoutRoomLabel }}
      <v-btn x-small text color="white" class="ml-2" @click="returnToMainRoom()">← Main Room</v-btn>
    </div>
  </div>
</template>

<script>
export default {
  name: "LiveStreamingRoom",

  data() {
    return {
      // ── Room connection ───────────────────────────────
      room: null,
      connected: false,
      mediaActivityTimer: null, // F06 DSD
      hasRemoteVideo: false,
      participantCount: 0,

      // ── Layout state ──────────────────────────────────
      isScreenSharing: false,
      screenShareTrack: null,
      remoteVideoTracks: [],  // [{ sid, name, track, participantSid }]
      footerHeight: 0, // B02: ความสูงจริงของ footer (measureFooter) — ใช้ใน roomWrapperStyle

      // ── Drawing overlay ───────────────────────────────
      drawCtx: null,

      // ── Local media (mic + camera) ────────────────────
      learnerMediaAllowed: true,   // host can revoke via learner-media-control message
      micMuted: false,
      camOff: false,
      localAudioTrack: null,
      localVideoTrack: null,

      // ── Analytics ────────────────────────────────────
      joinTime: null,

      // ── Chat ─────────────────────────────────────────
      chatMessage: "",
      chatMessages: [],
      sendingChat: false,
      chatEncoder: null,
      chatDecoder: null,
      chatSeenIds: null,
      chatPollInterval: null,
      replyingToChatID: null,
      replyingToName: null,
      replyingToText: null,

      // ── Emoji Reactions ──────────────────────────────
      emojis: ['👋', '👏', '❤️', '👍', '😂', '🤔'],
      floatingEmojis: [],
      emojiIdCounter: 0,

      // ── Tabs ─────────────────────────────────────────
      selectedTab: 0, // 0 = Chat, 1 = Poll

      // ── Poll ─────────────────────────────────────────
      pollData: null,
      isPollLoading: false,
      selectedPollOptions: [],
      pollTimerSeconds: 0,
      hasVoted: false,
      isVoting: false,
      pollTimedOut: false,
      pollResults: [],
      pollTimerInterval: null,
      currentPollID: null,
      brokenPollImages: {}, // B01: optionID → true เมื่อรูปโหลดไม่ได้ (ซ่อนรูป เหลือข้อความ)

      // ── Participants ──────────────────────────────────
      participants: [],
      participantLoading: false,

      // ── Q&A ──────────────────────────────────────────
      questions: [],
      qaLoading: false,
      newQuestion: "",
      submittingQuestion: false,
      // B02
      qaPollInterval: null,
      qaFetching: false,   // มี GetSessionQA ค้างอยู่ → รอบถัดไปข้าม (D11)
      qaLoadedOnce: false, // เคยโหลดสำเร็จแล้ว → ไม่แสดง spinner / error ของ poll เงียบ (D28)
      qaSeq: 0,            // ทิ้งผล poll ที่เริ่มก่อนค่าล่าสุด (D16)
      qaError: "",         // error ตอนส่ง (ใต้ช่องพิมพ์)
      qaLoadError: "",     // error ตอนโหลด (ในรายการ)

      // ── Recordings ───────────────────────────────────
      recordings: [],
      recordingsLoading: false,

      // ── Breakout Rooms ───────────────────────────────
      breakoutActive: false,
      assignedBreakoutRoomID: null,
      breakoutToken: null,
      parentRoomToken: null,
      parentLiveSessionID: null,
      parentLiveKitUrl: null,
      parentRoomID: null,
      breakoutPollInterval: null,
      showBreakoutModal: false,
      breakoutRoomLabel: "",
      breakoutCountdown: 10,
      joiningBreakout: false,
      isSwitchingRoom: false,
      disconnectRedirectSuppressUntil: 0,
      breakoutJoinRequested: false,
      breakoutJoinCompleted: false,
      isDedicatedBreakoutPage: false,
      breakoutJoinLockKey: "seed_breakout_join_lock",
    };
  },

  async mounted() {
    this.getFavourite();
    this.getNotificationCount();

    this.chatEncoder = new TextEncoder();
    this.chatDecoder = new TextDecoder();
    this.chatSeenIds = new Set();

    // When opened as a dedicated breakout page, hydrate breakout context from query params
    this.isDedicatedBreakoutPage = this.$route.query.isBreakout === '1';
    if (this.isDedicatedBreakoutPage) {
      this.breakoutActive = true;
      this.assignedBreakoutRoomID = this.$route.query.breakoutRoomID || null;
      this.breakoutRoomLabel = this.$route.query.breakoutRoomLabel || 'Breakout Room';
      this.parentRoomToken = this.$route.query.parentToken || null;
      this.parentLiveKitUrl = this.$route.query.parentLiveKitUrl || null;
      this.parentLiveSessionID = this.$route.query.parentLiveSessionID || this.$route.query.liveSessionID || null;
      this.parentRoomID = this.$route.query.parentRoomID || null;
    } else {
      // Clear stale breakout recovery keys on normal viewer entry so old assignments do not reappear
      sessionStorage.removeItem('breakout_room_id');
      sessionStorage.removeItem('breakout_room_label');
      sessionStorage.removeItem(this.breakoutJoinLockKey);
      this.breakoutJoinRequested = false;
      this.breakoutJoinCompleted = false;
      this.breakoutActive = false;
      this.assignedBreakoutRoomID = null;
      this.breakoutRoomLabel = '';
    }

    // B02: วัด footer ก่อนเชื่อม LiveKit · RO จับ Navbar เปลี่ยนจากซ่อนเป็นแสดงหลัง mount / ฟอนต์โหลด / footer ตัดบรรทัด (U7) — ไม่ต้องมี window.resize
    this.measureFooter();
    this.$nextTick(this.measureFooter);
    const footerEl = document.querySelector('footer.v-footer.foot-class');
    if (footerEl) {
      this._footerResizeObserver = new ResizeObserver(() => this.measureFooter());
      this._footerResizeObserver.observe(footerEl);
    }

    await this.connectRoom();
    this.startChatPolling();
    if (!this.isDedicatedBreakoutPage && !sessionStorage.getItem(this.breakoutJoinLockKey)) {
      this.startBreakoutScan();  // background poll for breakout assignment
    }
    if (this.isDedicatedBreakoutPage) {
      this.startBreakoutEndWatch();  // poll so user returns to main when host ends breakout
    }

    // Show a one-time notice when arriving back in the main room after a breakout ended
    if (!this.isDedicatedBreakoutPage && sessionStorage.getItem('breakout_ended_notice')) {
      sessionStorage.removeItem('breakout_ended_notice');
      this.chatMessages.push({
        id: null, sender: "System",
        text: "Breakout ended — you have been returned to the main session.",
        time: this.formatTime(new Date().toISOString()),
        mine: false, replyTo: null,
      });
      this.scrollChatToBottom();
    }

    // Keep draw canvas sized to its container
    this._stageResizeObserver = new ResizeObserver(() => { this.resizeDrawCanvas(); });
    const stage = this.$el.querySelector('.main-stage');
    if (stage) this._stageResizeObserver.observe(stage);

    globalThis.addEventListener("beforeunload", this.handleBeforeUnload);
  },

  // B01
  computed: {
    // ช่องรูป 32×32 แสดงทุกแถวผลเมื่อโพลมีรูปอย่างน้อย 1 ตัวเลือก → แถบผลตรงแนวกัน
    pollResultsHaveImages() {
      return this.pollResults.some((r) => !!r.optionImage);
    },
    // B02: ความสูงห้อง = จอ − app bar (reactive, ถูกแม้ Navbar ซ่อน — U7) − footer จริง → footer ไม่ทับ (D13 ทาง A)
    roomWrapperStyle() {
      const top = this.$vuetify.application.top || 64;
      return { height: `calc(100vh - ${top}px - ${this.footerHeight}px)` };
    },
  },

  watch: {
    isScreenSharing() {
      // When switching between grid/screen-share layout, re-attach all video tracks
      this.$nextTick(() => {
        this.remoteVideoTracks.forEach((rv) => {
          this.attachRemoteVideo(rv.sid, rv.track);
        });
        if (this.isScreenSharing && this.screenShareTrack && this.$refs.screenShareVideo) {
          this.screenShareTrack.attach(this.$refs.screenShareVideo);
        }
        this.attachLocalVideoToGrid();
      });
    },
    // B02: เริ่ม/หยุด poll Q&A ตามแท็บ (D18 — ถูกต้องแม้มีโค้ดอื่นสลับแท็บ)
    selectedTab(v) {
      if (v === 2) this.startQaPolling();
      else this.stopQaPolling();
    },
  },

  async beforeDestroy() {
    this.stopMediaActivity(); // F06 DSD
    globalThis.removeEventListener("beforeunload", this.handleBeforeUnload);
    this.stopChatPolling();
    this.stopQaPolling(); // B02
    this.clearPollTimer();
    this.stopBreakoutScan();
    this.stopBreakoutEndWatch();
    if (this._breakoutCountdownTimer) { clearInterval(this._breakoutCountdownTimer); this._breakoutCountdownTimer = null; }
    if (this._stageResizeObserver) this._stageResizeObserver.disconnect();
    if (this._footerResizeObserver) this._footerResizeObserver.disconnect(); // B02
    if (this.localAudioTrack) { try { this.localAudioTrack.stop(); } catch (e) { console.warn(e); } }
    if (this.localVideoTrack) { try { this.localVideoTrack.stop(); } catch (e) { console.warn(e); } }
    if (this.room) {
      await this.postLeaveReport();
      await this.room.disconnect();
    }
  },

  methods: {
    // ════════════════════════════════════════════════════
    //  Room connection
    // ════════════════════════════════════════════════════
    async connectRoom(targetUrl = null, targetToken = null) {
      const { Room, RoomEvent, Track } = await import("livekit-client");
      const resolvedUrl = targetUrl || this.$route.query.liveKitUrl;
      const resolvedToken = targetToken || this.$route.query.token;
      const isBreakoutConnect = !!(targetUrl && targetToken);
      if (!resolvedUrl || !resolvedToken) throw new Error("Missing room credentials");

      this.room = new Room();

      // Track subscribed — attach to correct element
      this.room.on(
        RoomEvent.TrackSubscribed,
        (track, publication) => {
          this.handleTrackSubscribed(track, publication, Track);
        }
      );

      // Track unsubscribed — detach + revert layout if screen share ended
      this.room.on(RoomEvent.TrackUnsubscribed, (track, publication) => {
        this.handleTrackUnsubscribed(track, publication);
      });

      // Incoming data channel messages — chat OR drawing
      this.room.on(RoomEvent.DataReceived, (payload) => {
        this._handleDataReceived(payload);
      });

      // Participant count updates
      this.room.on(RoomEvent.ParticipantConnected, () => {
        this.participantCount = this.room.remoteParticipants.size + 1;
      });
      this.room.on(RoomEvent.ParticipantDisconnected, () => {
        this.participantCount = this.room.remoteParticipants.size + 1;
      });

      // Room disconnected (kicked / host ended / network drop)
      this.room.on(RoomEvent.Disconnected, () => {
        if (this.isSwitchingRoom || this.joiningBreakout || Date.now() < this.disconnectRedirectSuppressUntil) return;
        // In a breakout room and the host ended it → go back to the main live room
        const hasParent = (this.parentRoomToken || this.$route.query.parentToken) &&
          (this.parentRoomID || this.$route.query.parentRoomID || this.$route.params.roomID || this.$route.query.roomID);
        if (this.breakoutActive && hasParent) {
          this.returnToMainRoom().catch(() => { });
          return;
        }
        this.postLeaveReport().catch(() => { });
        this.stopMediaActivity(); // F06 DSD
        this.connected = false;
        this.$router.push("/live");
      });

      await this.room.connect(resolvedUrl, resolvedToken);

      this.connected = true;
      this.startMediaActivity(); // F06 DSD — อยู่ในห้องไลฟ์ = activity
      this.participantCount = this.room.remoteParticipants.size + 1;

      // Capture refs NOW — v-show=connected means elements are in DOM
      // but we need $nextTick to guarantee Vue has applied the show state
      await this.$nextTick();

      // Initialize draw canvas
      const canvas = this.$refs.drawCanvas;
      if (canvas) {
        this.resizeDrawCanvas();
        this.drawCtx = canvas.getContext('2d');
      }

      // TrackSubscribed fires during connect() before DOM refs exist.
      // Re-attach any tracks that were already published before we captured the refs.
      this.room.remoteParticipants.forEach((participant) => {
        // Audio — must attach here too or the viewer hears nothing
        participant.audioTrackPublications.forEach((pub) => {
          if (pub.track && pub.isSubscribed) {
            pub.track.attach();
          }
        });
        // Video
        participant.videoTrackPublications.forEach((pub) => {
          if (pub.track && pub.isSubscribed) {
            this.hasRemoteVideo = true;
            const pName = participant.name || participant.identity || 'Participant';
            if (pub.source === "screen_share") {
              this.isScreenSharing = true;
              this.screenShareTrack = pub.track;
              this.$nextTick(() => {
                if (this.$refs.screenShareVideo) {
                  pub.track.attach(this.$refs.screenShareVideo);
                }
              });
            } else {
              const sid = pub.trackSid || pub.track.sid || (participant.sid + '_cam');
              // Avoid duplicates — check by sid and by participant
              const existingBySid = this.remoteVideoTracks.some(rv => rv.sid === sid);
              const existingByParticipant = this.remoteVideoTracks.find(rv => rv.participantSid === participant.sid);
              if (existingByParticipant) {
                existingByParticipant.track = pub.track;
                existingByParticipant.sid = sid;
              } else if (!existingBySid) {
                this.remoteVideoTracks.push({
                  sid,
                  name: pName,
                  track: pub.track,
                  participantSid: participant.sid,
                });
              }
              this.$nextTick(() => {
                this.attachRemoteVideo(sid, pub.track);
              });
            }
          }
        });
      });

      await this.postJoinReport();
      await this.publishLocalMedia();
      // Fallback check: catch assignments missed due to late join or network drop
      if (!isBreakoutConnect) {
        await this.checkBreakoutAssignment();
      }
    },

    async _handleDataReceived(payload) {
      try {
        const text = this.chatDecoder.decode(payload);
        const parsed = JSON.parse(text);

        if (parsed.type === "draw") {
          this.renderDrawStroke(parsed);
          return;
        }

        if (parsed.type === "draw-clear") {
          if (this.drawCtx) {
            const c = this.$refs.drawCanvas;
            this.drawCtx.clearRect(0, 0, c.width, c.height);
            this.drawCtx.beginPath();
          }
          return;
        }

        if (parsed.type === "learner-media-control") {
          await this._handleLearnerMediaControl(parsed.allowed);
          return;
        }

        if (parsed.message) return;

        if (parsed.type === "breakout-start") {
          this._handleBreakoutStart(parsed.rooms);
          return;
        }

        if (parsed.type === "breakout-end" || parsed.type === "breakout-ended") {
          await this._handleBreakoutEnd();
          return;
        }
      } catch (e) {
        console.warn('[LiveStreamingRoom] DataReceived parse:', e);
      }
    },

    async _handleLearnerMediaControl(allowed) {
      this.learnerMediaAllowed = !!allowed;
      if (allowed) {
        if (!this.localAudioTrack && !this.localVideoTrack) {
          this.publishLocalMedia();
        }
      } else {
        await this._disableLocalTracks();
      }
    },

    async _disableLocalTracks() {
      if (this.localAudioTrack) {
        try { await this.room.localParticipant.setMicrophoneEnabled(false); } catch (e) { console.warn(e); }
        try { this.localAudioTrack.stop(); } catch (e) { console.warn(e); }
        this.localAudioTrack = null;
      }
      if (this.localVideoTrack) {
        try { await this.room.localParticipant.setCameraEnabled(false); } catch (e) { console.warn(e); }
        try { this.localVideoTrack.stop(); } catch (e) { console.warn(e); }
        this.localVideoTrack = null;
      }
      this.micMuted = false;
      this.camOff = false;
    },

    _handleBreakoutStart(rawRooms) {
      if (this.breakoutActive || this.assignedBreakoutRoomID || this.joiningBreakout) return;
      const myUserID = Number.parseInt(localStorage.getItem("UserID"));
      const rooms = Array.isArray(rawRooms) ? rawRooms : [];
      if (!rooms.length) return;
      let assigned = rooms.find(r =>
        Array.isArray(r.participants) && r.participants.some(p =>
          Number.parseInt(p.userID || p.userId || p) === myUserID
        )
      );
      if (!assigned) assigned = rooms[myUserID % rooms.length];
      if (assigned) {
        this.assignedBreakoutRoomID = assigned.breakoutRoomID || assigned.id;
        this.breakoutRoomLabel = assigned.roomName || assigned.roomLabel || `Breakout Room ${assigned.groupIndex || assigned.roomNumber || ""}`;
        this.stopBreakoutScan();
        this.startBreakoutInvitation();
      }
    },

    async _handleBreakoutEnd() {
      if (this.breakoutActive) await this.returnToMainRoom();
    },

    // ════════════════════════════════════════════════════
    //  Track subscription — multi-participant grid logic
    // ════════════════════════════════════════════════════
    handleTrackSubscribed(track, publication, Track) {
      // Audio track — attach() auto-creates an <audio> element and plays it
      if (track.kind === "audio") {
        track.attach();
        return;
      }

      if (track.kind !== "video") return;

      this.hasRemoteVideo = true;

      // Find the participant who owns this track
      let participantName = 'Participant';
      let participantSid = '';
      if (this.room && this.room.remoteParticipants) {
        this.room.remoteParticipants.forEach((p) => {
          p.videoTrackPublications.forEach((pub) => {
            if (pub.trackSid === publication.trackSid) {
              participantName = p.name || p.identity || 'Participant';
              participantSid = p.sid;
            }
          });
        });
      }

      if (publication.source === Track.Source.ScreenShare) {
        // Screen share → full main stage
        this.isScreenSharing = true;
        this.screenShareTrack = track;
        this.$nextTick(() => {
          if (this.$refs.screenShareVideo) {
            track.attach(this.$refs.screenShareVideo);
          }
        });
      } else {
        // Camera feed — add to grid
        const sid = publication.trackSid || track.sid || (participantSid + '_cam');
        // Avoid duplicates — check both by sid and by participantSid (one cam per participant)
        const existingBySid = this.remoteVideoTracks.some(rv => rv.sid === sid);
        const existingByParticipant = this.remoteVideoTracks.find(rv => rv.participantSid === participantSid && participantSid);
        if (existingByParticipant) {
          // Same participant re-published camera — update the track reference
          existingByParticipant.track = track;
          existingByParticipant.sid = sid;
          this.$nextTick(() => {
            this.attachRemoteVideo(sid, track);
          });
        } else if (!existingBySid) {
          this.remoteVideoTracks.push({
            sid,
            name: participantName,
            track,
            participantSid,
          });
          this.$nextTick(() => {
            this.attachRemoteVideo(sid, track);
          });
        }
      }
    },

    attachRemoteVideo(sid, track) {
      const refKey = 'rv_' + sid;
      const el = this.$refs[refKey];
      if (el) {
        const videoEl = Array.isArray(el) ? el[0] : el;
        if (videoEl) track.attach(videoEl);
      }
    },

    handleTrackUnsubscribed(track, publication) {
      track.detach();

      if (publication && publication.source === "screen_share") {
        // Screen share ended
        this.isScreenSharing = false;
        this.screenShareTrack = null;
        // Re-attach camera tracks to grid (Vue re-renders, so $nextTick to attach)
        this.$nextTick(() => {
          this.remoteVideoTracks.forEach((rv) => {
            this.attachRemoteVideo(rv.sid, rv.track);
          });
          this.attachLocalVideoToGrid();
        });
      } else {
        // Camera track removed — remove from array
        const sid = publication ? (publication.trackSid || track.sid) : track.sid;
        this.remoteVideoTracks = this.remoteVideoTracks.filter(rv => rv.sid !== sid);
      }

      // Check if any video tracks remain
      let anyVideo = false;
      this.room.remoteParticipants.forEach((p) => {
        p.videoTrackPublications.forEach((pub) => {
          if (pub.track) anyVideo = true;
        });
      });
      this.hasRemoteVideo = anyVideo;
    },

    // ════════════════════════════════════════════════════
    //  Leave
    // ════════════════════════════════════════════════════
    async leaveRoom() {
      this.stopMediaActivity(); // F06 DSD
      try { await this.postLeaveReport(); } catch (e) { console.warn("[leave]", e); }
      if (this.localAudioTrack) { try { this.localAudioTrack.stop(); } catch (e) { console.warn(e); } }
      if (this.localVideoTrack) { try { this.localVideoTrack.stop(); } catch (e) { console.warn(e); } }
      if (this.room) { try { await this.room.disconnect(); } catch (e) { console.warn(e); } }
      this.$router.push("/live");
    },

    // F06 DSD — อยู่ในห้องไลฟ์นับเป็น activity (Q-T1 = ก)
    startMediaActivity() {
      this.notifyMediaActivity();
      if (this.mediaActivityTimer) return;
      this.mediaActivityTimer = setInterval(() => {
        if (this.connected) this.notifyMediaActivity();
      }, 30000);
    },
    stopMediaActivity() {
      clearInterval(this.mediaActivityTimer);
      this.mediaActivityTimer = null;
    },

    handleBeforeUnload() {
      const payload = JSON.stringify({
        liveSessionID: this.$route.query.liveSessionID,
        userID: Number.parseInt(localStorage.getItem("UserID")),
        companyId: localStorage.getItem("companyID"),
        leaveTime: new Date().toISOString(),
      });
      navigator.sendBeacon(
        `${this.web_url}LiveSession/LeaveLiveReport`,
        new Blob([payload], { type: "application/json" })
      );
    },

    // ════════════════════════════════════════════════════
    //  Analytics
    // ════════════════════════════════════════════════════
    async postJoinReport() {
      this.joinTime = Date.now();
      await this.$axios.post(`${this.web_url}LiveSession/JoinLiveReport`, {
        liveSessionID: this.$route.query.liveSessionID,
        userID: Number.parseInt(localStorage.getItem("UserID")),
        companyId: localStorage.getItem("companyID"),
        joinTime: new Date(this.joinTime).toISOString(),
      });
    },

    async postLeaveReport() {
      if (!this.joinTime) return;
      await this.$axios.post(`${this.web_url}LiveSession/LeaveLiveReport`, {
        liveSessionID: this.$route.query.liveSessionID,
        userID: Number.parseInt(localStorage.getItem("UserID")),
        companyId: localStorage.getItem("companyID"),
        leaveTime: new Date().toISOString(),
        durationMs: Date.now() - this.joinTime,
      });
      this.joinTime = null;
    },

    // ════════════════════════════════════════════════════
    //  Chat — dual delivery
    // ════════════════════════════════════════════════════
    async sendChat() {
      const text = this.chatMessage.trim();
      if (!text) return;

      this.sendingChat = true;
      const userName = localStorage.getItem("userName");
      const time = new Date().toISOString();

      try {
        if (this.replyingToChatID) {
          await this._sendChatReply(text, time);
        } else {
          await this._sendChatTopLevel(text, time, userName);
        }
        this.chatMessage = "";
        this.scrollChatToBottom();
      } catch (err) {
        console.error("[chat] sendChat failed:", err);
      } finally {
        this.sendingChat = false;
      }
    },

    async _sendChatReply(text, time) {
      const parentChatID = this.replyingToChatID;
      const replySnapshot = { name: this.replyingToName, text: this.replyingToText };
      const replyRes = await this.$axios.post(`${this.web_url}LiveSession/AddSessionChatReply`, {
        liveSessionChatID: parentChatID,
        userID: Number.parseInt(localStorage.getItem("UserID")),
        message: text,
      });
      const savedReplyId = replyRes.data && replyRes.data.data &&
        (replyRes.data.data.replyID || replyRes.data.data.ReplyID || replyRes.data.data.id);
      if (savedReplyId) this.chatSeenIds.add('reply-' + savedReplyId);
      this.replyingToChatID = null;
      this.replyingToName = null;
      this.replyingToText = null;
      this.chatMessages.push({
        id: savedReplyId ? 'reply-' + savedReplyId : null,
        chatID: parentChatID,
        sender: "You",
        text,
        time: this.formatTime(time),
        mine: true,
        replyTo: replySnapshot,
        profileImage: localStorage.getItem("profileImage") || null,
        initials: this.getInitials(localStorage.getItem("userName") || "You"),
        avatarColor: "#A6CC39",
      });
    },

    async _sendChatTopLevel(text, time, userName) {
      try {
        if (this.room && this.room.localParticipant) {
          const payload = this.chatEncoder.encode(
            JSON.stringify({ message: text, time, sender: userName })
          );
          await this.room.localParticipant.publishData(payload, { reliable: true });
        }
      } catch (rtcErr) {
        console.warn("[chat] WebRTC publishData skipped:", rtcErr.message);
      }
      const chatRes = await this.$axios.post(`${this.web_url}LiveSession/AddSessionChat`, {
        liveSessionID: this.$route.query.liveSessionID,
        userID: Number.parseInt(localStorage.getItem("UserID")),
        companyId: localStorage.getItem("companyID"),
        message: text,
        senderName: userName,
        sentTime: time,
      });
      const savedId = chatRes.data && chatRes.data.data && chatRes.data.data.chatID;
      if (savedId) this.chatSeenIds.add('chat-' + savedId);
      this.chatMessages.push({
        id: savedId || null,
        chatID: savedId || null,
        sender: "You",
        text,
        time: this.formatTime(time),
        mine: true,
        profileImage: localStorage.getItem("profileImage") || null,
        initials: this.getInitials(localStorage.getItem("userName") || "You"),
        avatarColor: "#A6CC39",
      });
    },

    startReply(msg) {
      // Always reply against the integer parent chat id (never a "reply-…" string id)
      this.replyingToChatID = msg.chatID || msg.id || null;
      this.replyingToName = msg.sender;
      this.replyingToText = msg.text;
      this.$nextTick(function () {
        const el = this.$el.querySelector('.chat-input-pill input, .chat-input-pill textarea');
        if (el) el.focus();
      }.bind(this));
    },

    resizeDrawCanvas() {
      const canvas = this.$refs.drawCanvas;
      if (!canvas) return;
      const stage = canvas.parentElement;
      canvas.width = stage ? stage.offsetWidth : canvas.offsetWidth;
      canvas.height = stage ? stage.offsetHeight : canvas.offsetHeight;
    },

    renderDrawStroke(data) {
      const ctx = this.drawCtx;
      if (!ctx) return;
      const canvas = this.$refs.drawCanvas;
      if (!canvas) return;
      const w = canvas.width;
      const h = canvas.height;
      // Coordinates are normalized (0-1) by the host — scale to our canvas size
      const x0 = (data.x0 ?? 0) * w;
      const y0 = (data.y0 ?? 0) * h;
      const x1 = (data.x1 ?? 0) * w;
      const y1 = (data.y1 ?? 0) * h;
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      ctx.lineTo(x1, y1);
      ctx.strokeStyle = data.color || 'red';
      ctx.lineWidth = data.lineWidth || 3;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
    },

    scrollChatToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.chatScroll;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },

    // ════════════════════════════════════════════════════
    //  Breakout Room — background scan + lifecycle
    // ════════════════════════════════════════════════════

    // Start polling GetBreakoutRooms every 5s to detect assignment
    startBreakoutScan() {
      this.stopBreakoutScan();
      this.breakoutScanInterval = setInterval(() => this.scanForBreakoutAssignment(), 5000);
    },

    stopBreakoutScan() {
      if (this.breakoutScanInterval) {
        clearInterval(this.breakoutScanInterval);
        this.breakoutScanInterval = null;
      }
    },

    // A breakout room counts as active unless its status flags it ended/closed.
    isBreakoutRoomActive(room) {
      if (!room || room.status === undefined || room.status === null) return true;
      const s = String(room.status).toLowerCase();
      return !(Number(room.status) === 2 || s === 'ended' || s === 'closed' || s === 'inactive');
    },

    // True while the post-breakout cooldown is in effect (suppresses re-assignment).
    isBreakoutCooldownActive() {
      const until = Number.parseInt(sessionStorage.getItem('breakout_cooldown_until') || '0', 10);
      if (!until) return false;
      if (Date.now() >= until) { sessionStorage.removeItem('breakout_cooldown_until'); return false; }
      return true;
    },

    // While inside a breakout room, poll so the user is pulled back to the
    // main session as soon as the host ends the breakout rooms.
    startBreakoutEndWatch() {
      this.stopBreakoutEndWatch();
      this._breakoutEndMisses = 0;
      this.breakoutEndWatchInterval = setInterval(() => this.checkBreakoutEnded(), 5000);
    },

    stopBreakoutEndWatch() {
      if (this.breakoutEndWatchInterval) {
        clearInterval(this.breakoutEndWatchInterval);
        this.breakoutEndWatchInterval = null;
      }
    },

    async checkBreakoutEnded() {
      if (!this.breakoutActive || this.isSwitchingRoom || this.joiningBreakout) return;
      try {
        const liveSessionID = this.parentLiveSessionID || this.$route.query.parentLiveSessionID || this.$route.query.liveSessionID;
        if (!liveSessionID) return;
        const res = await this.$axios.get(`${this.web_url}LiveSession/GetBreakoutRooms/${liveSessionID}`);
        const rawRooms = res.data?.data ?? res.data;
        const rooms = Array.isArray(rawRooms) ? rawRooms : [];
        const myRoomID = this.assignedBreakoutRoomID;
        const myRoom = rooms.find(r => (r.breakoutRoomID || r.id) === myRoomID);
        // Ended when no rooms remain, my room disappeared, or its status flags it closed
        const statusEnded = myRoom && myRoom.status !== undefined &&
          (Number(myRoom.status) === 2 || String(myRoom.status).toLowerCase() === 'ended' || String(myRoom.status).toLowerCase() === 'closed');
        // Ended when every room reports a closed status (host ended all rooms)
        const allRoomsEnded = rooms.length > 0 && rooms.every(r =>
          r.status !== undefined &&
          (Number(r.status) === 2 || String(r.status).toLowerCase() === 'ended' || String(r.status).toLowerCase() === 'closed')
        );
        const ended = !rooms.length || !myRoom || statusEnded || allRoomsEnded;
        if (ended) {
          // Require two consecutive signals to avoid acting on a transient blip
          this._breakoutEndMisses = (this._breakoutEndMisses || 0) + 1;
          if (this._breakoutEndMisses >= 2) {
            this.stopBreakoutEndWatch();
            await this.returnToMainRoom();
          }
        } else {
          this._breakoutEndMisses = 0;
        }
      } catch (e) {
        console.warn('[LiveStreamingRoom] checkBreakoutEnded:', e);
      }
    },

    // One-shot check called after connectRoom and after returnToMainRoom
    async checkBreakoutAssignment() {
      if (this.isDedicatedBreakoutPage || this.isBreakoutCooldownActive() || sessionStorage.getItem(this.breakoutJoinLockKey) || this.breakoutActive || this.assignedBreakoutRoomID || this.joiningBreakout || this.breakoutJoinRequested || this.breakoutJoinCompleted) return;
      try {
        const liveSessionID = this.parentLiveSessionID || this.$route.query.liveSessionID;
        const myUserID = Number.parseInt(localStorage.getItem('UserID'));
        const res = await this.$axios.get(`${this.web_url}LiveSession/GetBreakoutRooms/${liveSessionID}`);
        const rawAllRooms = res.data?.data ?? res.data;
        const allRooms = Array.isArray(rawAllRooms) ? rawAllRooms : [];
        // Only consider rooms that are still active — ignore ended/closed ones
        const rooms = allRooms.filter(r => this.isBreakoutRoomActive(r));
        if (!rooms.length) {
          this.assignedBreakoutRoomID = null;
          this.breakoutRoomLabel = '';
          sessionStorage.removeItem('breakout_room_id');
          sessionStorage.removeItem('breakout_room_label');
          return;
        }
        // Try explicit participant assignment first
        let assigned = rooms.find(r =>
          Array.isArray(r.participants) && r.participants.some(p =>
            Number.parseInt(p.userID || p.userId || p.memberID || p) === myUserID
          )
        );
        // Fall back to round-robin when backend returns no per-user assignments
        if (!assigned && rooms.every(r => !r.participants || r.participants.length === 0)) {
          assigned = rooms[myUserID % rooms.length];
        }
        if (assigned) {
          this.stopBreakoutScan();
          this.assignedBreakoutRoomID = assigned.breakoutRoomID || assigned.id;
          this.breakoutRoomLabel = assigned.roomName || assigned.roomLabel || `Breakout Room ${assigned.groupIndex || assigned.roomNumber || ''}`;
          this.startBreakoutInvitation();
        }
      } catch (e) {
        console.warn('[LiveStreamingRoom] checkBreakoutAssignment:', e);
      }
    },

    async scanForBreakoutAssignment() {
      if (this.isDedicatedBreakoutPage || this.isBreakoutCooldownActive() || sessionStorage.getItem(this.breakoutJoinLockKey) || this.breakoutActive || this.assignedBreakoutRoomID || this.joiningBreakout || this.breakoutJoinRequested || this.breakoutJoinCompleted) {
        this.stopBreakoutScan();
        return;
      }
      try {
        const liveSessionID = this.parentLiveSessionID || this.$route.query.liveSessionID;
        const myUserID = Number.parseInt(localStorage.getItem("UserID"));
        const res = await this.$axios.get(`${this.web_url}LiveSession/GetBreakoutRooms/${liveSessionID}`);
        const rawAllRooms = res.data?.data ?? res.data;
        const allRooms = Array.isArray(rawAllRooms) ? rawAllRooms : [];
        // Only consider rooms that are still active — ignore ended/closed ones
        const rooms = allRooms.filter(r => this.isBreakoutRoomActive(r));
        if (!rooms.length) {
          this.assignedBreakoutRoomID = null;
          this.breakoutRoomLabel = '';
          sessionStorage.removeItem('breakout_room_id');
          sessionStorage.removeItem('breakout_room_label');
          return;
        }
        // Try explicit participant assignment first
        let assigned = rooms.find(r =>
          Array.isArray(r.participants) && r.participants.some(p =>
            Number.parseInt(p.userID || p.userId || p) === myUserID
          )
        );
        // Fall back to round-robin when backend returns no per-user assignments
        if (!assigned && rooms.every(r => !r.participants || r.participants.length === 0)) {
          assigned = rooms[myUserID % rooms.length];
        }
        if (assigned) {
          this.stopBreakoutScan();
          this.assignedBreakoutRoomID = assigned.breakoutRoomID || assigned.id;
          this.breakoutRoomLabel = assigned.roomName || assigned.roomLabel || `Breakout Room ${assigned.groupIndex || assigned.roomNumber || ""}`;
          this.startBreakoutInvitation();
        }
      } catch (e) {
        console.warn('[LiveStreamingRoom] scanForBreakoutAssignment:', e);
      }
    },

    // Show countdown modal
    startBreakoutInvitation() {
      if (this.isDedicatedBreakoutPage || sessionStorage.getItem(this.breakoutJoinLockKey) || this.breakoutActive || this.joiningBreakout || this.breakoutJoinRequested || this.breakoutJoinCompleted || this.showBreakoutModal || this._breakoutCountdownTimer) return;
      this.breakoutCountdown = 10;
      this.showBreakoutModal = true;
      this._breakoutCountdownTimer = setInterval(() => {
        this.breakoutCountdown -= 1;
        if (this.breakoutCountdown <= 0) {
          clearInterval(this._breakoutCountdownTimer);
          this._breakoutCountdownTimer = null;
          this.joinBreakoutNow();
        }
      }, 1000);
    },

    dismissBreakout() {
      this.showBreakoutModal = false;
      if (this._breakoutCountdownTimer) { clearInterval(this._breakoutCountdownTimer); this._breakoutCountdownTimer = null; }
      // Keep assignedBreakoutRoomID so the "Join Breakout" button stays visible in the controls bar
    },

    async joinBreakoutNow() {
      if (!this.assignedBreakoutRoomID) return;
      if (this.joiningBreakout || this.breakoutJoinRequested || this.breakoutJoinCompleted || sessionStorage.getItem(this.breakoutJoinLockKey)) return;
      this.joiningBreakout = true;
      this.breakoutJoinRequested = true;
      this.disconnectRedirectSuppressUntil = Date.now() + 20000;
      this.showBreakoutModal = false;
      if (this._breakoutCountdownTimer) { clearInterval(this._breakoutCountdownTimer); this._breakoutCountdownTimer = null; }
      this.stopBreakoutScan();

      try {
        sessionStorage.setItem(this.breakoutJoinLockKey, JSON.stringify({
          liveSessionID: this.$route.query.liveSessionID || null,
          breakoutRoomID: this.assignedBreakoutRoomID,
          timestamp: Date.now(),
        }));

        const { newToken, breakoutUrl } = await this._fetchBreakoutToken(this.assignedBreakoutRoomID);
        this.stopBreakoutScan();
        this.breakoutJoinCompleted = true;

        // Save parent context BEFORE any state mutation
        this.parentRoomToken = this.$route.query.token;
        this.parentLiveKitUrl = this.$route.query.liveKitUrl;
        this.parentLiveSessionID = this.$route.query.liveSessionID;
        this.parentRoomID = this.$route.params.roomID || this.$route.query.roomID || null;

        this.stopChatPolling();
        if (this.localAudioTrack) { try { this.localAudioTrack.stop(); } catch (e) { console.warn(e); } this.localAudioTrack = null; }
        if (this.localVideoTrack) { try { this.localVideoTrack.stop(); } catch (e) { console.warn(e); } this.localVideoTrack = null; }

        this.isSwitchingRoom = true;
        await this.room.disconnect();

        sessionStorage.setItem('breakout_room_id', this.assignedBreakoutRoomID);
        sessionStorage.setItem('breakout_room_label', this.breakoutRoomLabel);

        await this.$router.push({
          name: 'live-breakout-room',
          params: { roomID: this.assignedBreakoutRoomID },
          query: {
            isBreakout: '1',
            token: newToken,
            liveKitUrl: breakoutUrl,
            roomID: this.assignedBreakoutRoomID,
            liveSessionID: this.$route.query.liveSessionID,
            breakoutRoomID: this.assignedBreakoutRoomID,
            breakoutRoomLabel: this.breakoutRoomLabel || 'Breakout Room',
            parentToken: this.parentRoomToken || '',
            parentLiveKitUrl: this.parentLiveKitUrl || '',
            parentLiveSessionID: this.parentLiveSessionID || this.$route.query.liveSessionID || '',
            parentRoomID: this.parentRoomID || this.$route.params.roomID || this.$route.query.roomID || '',
          },
        });
      } catch (err) {
        await this._handleBreakoutJoinError(err);
      } finally {
        this.joiningBreakout = false;
        this.isSwitchingRoom = false;
        if (!this.breakoutActive && !sessionStorage.getItem(this.breakoutJoinLockKey)) {
          this.breakoutJoinRequested = false;
          this.breakoutJoinCompleted = false;
          this.disconnectRedirectSuppressUntil = 0;
        }
      }
    },

    async _fetchBreakoutToken(breakoutRoomID) {
      const userID = Number.parseInt(localStorage.getItem("UserID"));
      const tokenRes = await this.$axios.post(`${this.web_url}LiveSession/GetBreakoutRoomToken`, {
        breakoutRoomID,
        userID,
      });
      const newToken = tokenRes.data?.data?.token || tokenRes.data?.token;
      const breakoutUrl = tokenRes.data?.data?.liveKitUrl || tokenRes.data?.liveKitUrl || this.$route.query.liveKitUrl;
      if (!newToken) throw new Error("No breakout token returned");
      if (!breakoutUrl) throw new Error("No breakout liveKitUrl returned");
      return { newToken, breakoutUrl };
    },

    async _handleBreakoutJoinError(err) {
      console.error("[breakout] joinBreakoutNow failed:", err);
      this.isSwitchingRoom = false;
      this.breakoutActive = false;
      if (!this.connected) {
        const parentToken = this.parentRoomToken || this.$route.query.token;
        const parentUrl = this.parentLiveKitUrl || this.$route.query.liveKitUrl;
        if (parentToken && parentUrl) {
          try {
            this.isSwitchingRoom = true;
            await this.connectRoom(parentUrl, parentToken);
            this.isSwitchingRoom = false;
            this.startChatPolling();
            this.startBreakoutScan();
          } catch (reconnectErr) {
            console.warn("[breakout] reconnect failed:", reconnectErr);
            this.isSwitchingRoom = false;
          }
        }
      }
      this.chatMessages.push({
        id: null, sender: "System",
        text: err?.response?.status === 404
          ? "Breakout room service is not available yet — please ask the administrator to update the server."
          : `Could not join breakout room: ${err?.message || "unknown error"}`,
        time: this.formatTime(new Date().toISOString()),
        mine: false, replyTo: null,
      });
      this.scrollChatToBottom();
      sessionStorage.removeItem(this.breakoutJoinLockKey);
    },

    async returnToMainRoom() {
      // Recover parent context from data fields or (dedicated breakout page) query params
      const savedParentToken = this.parentRoomToken || this.$route.query.parentToken || null;
      const savedParentUrl = this.parentLiveKitUrl || this.$route.query.parentLiveKitUrl || null;
      const savedParentRoomID = this.parentRoomID || this.$route.query.parentRoomID || this.$route.params.roomID || this.$route.query.roomID || null;
      const savedParentSessionID = this.parentLiveSessionID || this.$route.query.parentLiveSessionID || this.$route.query.liveSessionID || '';
      if (!savedParentToken || !savedParentRoomID) return;

      // Keep these guards SET through teardown + navigation so the dying
      // component's Disconnected handler can never redirect to /live.
      this.isSwitchingRoom = true;
      this.disconnectRedirectSuppressUntil = Date.now() + 20000;
      this.showBreakoutModal = false;
      if (this._breakoutCountdownTimer) { clearInterval(this._breakoutCountdownTimer); this._breakoutCountdownTimer = null; }
      this.stopBreakoutScan();
      this.stopBreakoutEndWatch();
      this.stopChatPolling();

      if (this.localAudioTrack) { try { this.localAudioTrack.stop(); } catch (e) { console.warn(e); } this.localAudioTrack = null; }
      if (this.localVideoTrack) { try { this.localVideoTrack.stop(); } catch (e) { console.warn(e); } this.localVideoTrack = null; }

      this.breakoutActive = false;
      this.stopMediaActivity(); // F06 DSD
      this.connected = false;

      // Clear breakout persistence so the main room does not re-trigger the old assignment
      sessionStorage.removeItem('breakout_room_id');
      sessionStorage.removeItem('breakout_room_label');
      sessionStorage.removeItem(this.breakoutJoinLockKey);
      // Suppress re-assignment briefly so the just-ended breakout cannot re-show Join
      sessionStorage.setItem('breakout_cooldown_until', String(Date.now() + 30000));
      // Let the main room show a one-time "breakout ended" notice
      sessionStorage.setItem('breakout_ended_notice', '1');

      try { await this.room.disconnect(); } catch (e) { console.warn('[LiveStreamingRoom] disconnect:', e); }

      // Navigate to the MAIN live room. A fresh component mounts and connects
      // with the parent credentials — no fragile in-place reconnect.
      await this.$router.replace({
        path: `/live/room/${savedParentRoomID}`,
        query: {
          token: savedParentToken,
          liveKitUrl: savedParentUrl || '',
          roomID: savedParentRoomID,
          liveSessionID: savedParentSessionID,
        },
      }).catch(() => { });
    },

    // ════════════════════════════════════════════════════
    //  Local media (viewer mic + camera)
    // ════════════════════════════════════════════════════
    async publishLocalMedia() {
      try {
        const { createLocalTracks } = await import("livekit-client");
        const tracks = await createLocalTracks({ audio: true, video: true });
        for (const track of tracks) {
          await this.room.localParticipant.publishTrack(track);
          if (track.kind === "audio") {
            this.localAudioTrack = track;
          } else if (track.kind === "video") {
            this.localVideoTrack = track;
            await this.$nextTick();
            this.attachLocalVideoToGrid();
          }
        }
      } catch (err) {
        // Permission denied or device unavailable — room stays connected
        console.warn("[local-media] Could not publish:", err.message);
      }
    },

    attachLocalVideoToGrid() {
      if (!this.localVideoTrack) return;
      // Detach from any previous element first
      this.localVideoTrack.detach();
      this.$nextTick(() => {
        // Attach to whichever local video ref is currently rendered
        const el = this.$refs.localVideoGrid || this.$refs.localVideoFilmstrip;
        if (el) {
          const videoEl = Array.isArray(el) ? el[0] : el;
          if (videoEl) this.localVideoTrack.attach(videoEl);
        }
      });
    },

    async toggleMic() {
      this.micMuted = !this.micMuted;
      try {
        await this.room.localParticipant.setMicrophoneEnabled(!this.micMuted);
      } catch (err) {
        console.warn("[toggleMic]", err.message);
      }
    },

    async toggleCam() {
      this.camOff = !this.camOff;
      try {
        await this.room.localParticipant.setCameraEnabled(!this.camOff);
        if (!this.camOff) {
          this.$nextTick(() => this.attachLocalVideoToGrid());
        }
      } catch (err) {
        console.warn("[toggleCam]", err.message);
      }
    },

    // ══════════════════════════════════════════════════
    //  Chat polling (catches host messages sent via HTTP)
    // ══════════════════════════════════════════════════
    startChatPolling() {
      this.stopChatPolling();
      // Run once immediately to load existing chat history, then every 2 s
      this.pollSessionChat();
      this.chatPollInterval = setInterval(() => this.pollSessionChat(), 2000);
    },

    stopChatPolling() {
      if (this.chatPollInterval) {
        clearInterval(this.chatPollInterval);
        this.chatPollInterval = null;
      }
    },

    async pollSessionChat() {
      const liveSessionID = this.$route.query.liveSessionID;
      if (!liveSessionID) return;
      try {
        const res = await this.$axios.get(
          `${this.web_url}LiveSession/GetSessionChat/${liveSessionID}`
        );
        const rawItems = res.data?.data ?? res.data;
        const items = Array.isArray(rawItems) ? rawItems : [];

        const myUserID = Number.parseInt(localStorage.getItem("UserID"));
        const myName = localStorage.getItem("userName") || "You";
        const newMsgs = [];

        // Server returns chats newest-first; process oldest-first so the
        // timeline reads top (old) → bottom (new), like the host chat.
        const ordered = [...items].reverse();

        ordered.forEach((item) => {
          const chatId = item.chatID || item.id || item.ID || item.ChatID;
          if (!chatId) return;

          const parentName = item.fullname || item.fullName || item.Fullname || item.senderName || "Host";
          const parentText = item.message || item.Message || "";
          const parentTime = item.createdDate || item.CreatedDate || item.sentTime || item.SentTime;

          // ── Top-level message ──────────────────────────
          const chatKey = 'chat-' + chatId;
          if (!this.chatSeenIds.has(chatKey)) {
            this.chatSeenIds.add(chatKey);
            const isMe = Number.parseInt(item.userID || item.UserID) === myUserID;
            newMsgs.push({
              id: chatId,
              chatID: chatId,
              sender: isMe ? "You" : parentName,
              text: parentText,
              time: this.formatTime(parentTime),
              mine: isMe,
              replyTo: null,
              profileImage: item.profileImage || item.ProfileImage || item.profilePic || item.avatar || null,
              initials: this.getInitials(isMe ? myName : parentName),
              avatarColor: isMe ? "#A6CC39" : this.getAvatarColor(parentName),
            });
          }

          // ── Nested replies (quote = the parent message) ──
          const rawReplies = item.replyList ?? item.ReplyList;
          const replies = Array.isArray(rawReplies) ? rawReplies : [];
          replies.forEach((rep) => {
            const replyId = rep.replyID || rep.ReplyID || rep.id || rep.ID;
            if (!replyId) return;
            const replyKey = 'reply-' + replyId;
            if (this.chatSeenIds.has(replyKey)) return;
            this.chatSeenIds.add(replyKey);

            const repIsMe = Number.parseInt(rep.userID || rep.UserID) === myUserID;
            const repName = rep.fullname || rep.fullName || rep.Fullname || "User";
            const repText = rep.message || rep.Message || "";
            const repTime = rep.createdDate || rep.CreatedDate || parentTime;
            const quoteName = rep.refReplyFullname || rep.RefReplyFullname || parentName;

            newMsgs.push({
              id: replyKey,
              chatID: chatId,
              sender: repIsMe ? "You" : repName,
              text: repText,
              time: this.formatTime(repTime),
              mine: repIsMe,
              replyTo: { name: quoteName, text: parentText },
              profileImage: rep.profileImage || rep.ProfileImage || null,
              initials: this.getInitials(repIsMe ? myName : repName),
              avatarColor: repIsMe ? "#A6CC39" : this.getAvatarColor(repName),
            });
          });
        });

        if (newMsgs.length) {
          newMsgs.forEach((m) => this.chatMessages.push(m));
          this.scrollChatToBottom();
        }
      } catch (_) {
        console.warn('[LiveStreamingRoom] pollSessionChat:', _);
      }
    },

    formatTime(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    },

    getInitials(name) {
      if (!name) return "?";
      return name.split(" ").filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join("") || "?";
    },

    getAvatarColor(name) {
      const colors = ["#4FC3F7", "#81C784", "#FFB74D", "#BA68C8", "#FF8A65", "#A1887F", "#4DD0E1"];
      let hash = 0;
      for (let i = 0; i < (name || "").length; i++) {
        hash = name.codePointAt(i) + ((hash << 5) - hash);
      }
      return colors[Math.abs(hash) % colors.length];
    },

    // ════════════════════════════════════════════════════
    //  Emoji Reactions
    // ════════════════════════════════════════════════════
    sendEmoji(emojiIndex, emoji) {
      if (!this.$route.query.liveSessionID) return;

      // Show floating animation
      this.emojiIdCounter++;
      const startX = 12 + (emojiIndex - 1) * 48;
      this.floatingEmojis.push({
        id: this.emojiIdCounter,
        emoji: emoji,
        startX: startX,
      });

      // Fire-and-forget API call
      const userID = Number.parseInt(localStorage.getItem("UserID"));
      this.$axios.post(`${this.web_url}LiveSession/AddLiveEmoji`, {
        liveSessionID: this.$route.query.liveSessionID,
        userID: userID,
        emoji: emojiIndex,
      });
    },

    removeFloatingEmoji(id) {
      this.floatingEmojis = this.floatingEmojis.filter((e) => e.id !== id);
    },

    // ════════════════════════════════════════════════════
    //  Tab Selection
    // ════════════════════════════════════════════════════
    selectTab(index) {
      this.selectedTab = index;
      // B02: Q&A (index 2) เริ่ม/หยุด polling ใน watch.selectedTab — ไม่เรียก fetchQA ที่นี่
      if (index === 1) {
        this.fetchParticipants();
      } else if (index === 3) {
        this.loadPoll();
      }
    },

    // ════════════════════════════════════════════════════
    //  Poll
    // ════════════════════════════════════════════════════
    async loadPoll() {
      const liveSessionID = this.$route.query.liveSessionID;
      if (!liveSessionID) return;

      this.isPollLoading = true;
      try {
        const response = await this.$axios.get(
          `${this.web_url}LiveSession/GetLatestPollForLearner/${liveSessionID}`
        );
        const data = response.data?.data;

        if (data) {
          const newPollID = data.pollID;

          if (newPollID !== this.currentPollID) {
            // NEW poll → reset all states for fresh voting
            this.currentPollID = newPollID;
            this.pollData = data;
            this.selectedPollOptions = [];
            this.hasVoted = false;
            this.pollTimedOut = false;
            this.pollResults = [];
            this.brokenPollImages = {}; // B01: โพลใหม่ → ล้างสถานะรูปเสียของโพลเก่า
            this.clearPollTimer();

            const answerTimer = data.answerTimer || 0;
            if (answerTimer > 0) {
              this.pollTimerSeconds = answerTimer;
              this.startPollTimer();
            }
          } else {
            // SAME poll → preserve voted/timeout state, just refresh data
            this.pollData = data;
          }
        } else {
          this.pollData = null;
        }
      } catch (error) {
        console.error("Failed to load poll:", error);
        this.pollData = null;
      } finally {
        this.isPollLoading = false;
      }
    },

    startPollTimer() {
      this.clearPollTimer();
      this.pollTimerInterval = setInterval(() => {
        if (this.pollTimerSeconds > 0) {
          this.pollTimerSeconds--;
        } else {
          this.clearPollTimer();
          if (this.hasVoted) return;
          this.onPollTimeout();
        }
      }, 1000);
    },

    clearPollTimer() {
      if (this.pollTimerInterval) {
        clearInterval(this.pollTimerInterval);
        this.pollTimerInterval = null;
      }
    },

    async onPollTimeout() {
      this.pollTimedOut = true;
      if (!this.pollData?.showResult) return;

      const pollID = this.pollData?.pollID;
      if (!pollID) return;

      try {
        const response = await this.$axios.get(
          `${this.web_url}LiveSession/GetPollResults/${pollID}`
        );
        const data = response.data?.data;
        if (data && data.options) {
          this.pollResults = this.mapPollResults(data.options); // B01
        }
      } catch (error) {
        console.error("Failed to load poll results:", error);
      }
    },

    formatTimer(seconds) {
      const m = String(Math.floor(seconds / 60)).padStart(2, "0");
      const s = String(seconds % 60).padStart(2, "0");
      return `${m}:${s}`;
    },

    togglePollOption(optionID) {
      if (this.pollTimerSeconds <= 0) return;
      const pollType = this.pollData?.pollType || 1;

      if (pollType === 1) {
        // Single select
        this.selectedPollOptions = [optionID];
      } else {
        // Multi select — toggle
        const index = this.selectedPollOptions.indexOf(optionID);
        if (index >= 0) {
          this.selectedPollOptions.splice(index, 1);
        } else {
          this.selectedPollOptions.push(optionID);
        }
      }
    },

    async submitVote() {
      if (this.selectedPollOptions.length === 0 || !this.pollData) return;

      this.isVoting = true;
      const pollID = this.pollData.pollID;
      const showResult = this.pollData.showResult || false;
      const userID = Number.parseInt(localStorage.getItem("UserID"));
      const memberID = localStorage.getItem("memberID") || "";

      try {
        const res = await this.$axios.post(`${this.web_url}LiveSession/VotePoll`, {
          livePollID: pollID,
          livePollOptionIDs: this.selectedPollOptions,
          userID: userID,
          createdBy: memberID,
        });

        this.hasVoted = true;
        this.clearPollTimer();

        if (showResult) {
          // B01: ผลจริงจาก response ของ VotePoll → สำรองด้วยการดึงผลโพล (ด้านล่าง) → [] (template แสดงการ์ด "vote recorded")
          let results = this.mapPollResults(res.data?.status == 0 ? res.data?.data?.options : null);
          if (results.length === 0) {
            try {
              const response = await this.$axios.get(
                `${this.web_url}LiveSession/GetPollResults/${pollID}`
              );
              results = this.mapPollResults(response.data?.data?.options);
            } catch (_) {
              console.warn('[LiveStreamingRoom] vote result fetch:', _);
              results = [];
            }
          }
          // B01: ผู้เรียนกดแท็บ Poll แล้วได้โพลใหม่ระหว่างรอ → ไม่เอาผลของโพลเก่าไปแสดง
          if (this.currentPollID === pollID) this.pollResults = results;
        }
      } catch (error) {
        console.error("Failed to submit vote:", error);
      } finally {
        this.isVoting = false;
      }
    },

    getResultPercentage(result) {
      const totalVotes = this.pollResults.reduce((sum, r) => sum + (r.voteCount || 0), 0);
      if (totalVotes === 0) return 0;
      return Math.round((result.voteCount / totalVotes) * 100);
    },

    // B01: ตัวเลือก/ผลที่มีรูปและรูปยังไม่เสีย
    hasPollImage(item) {
      return !!(item && item.optionImage) && !this.brokenPollImages[item.optionID];
    },

    // B01: รูปโหลดไม่ได้ → ถอด <img> (v-if) ไม่ให้เห็นไอคอนรูปแตก
    markPollImageBroken(optionID) {
      if (!optionID) return;
      this.$set(this.brokenPollImages, optionID, true);
    },

    // B01: map ผลโหวตจริง + ใช้ optionImage (SAS) ตัวเดียวกับที่ปุ่มตัวเลือกแสดงอยู่ก่อน (browser ใช้ cache — SAS เปลี่ยนทุก request E10)
    mapPollResults(options) {
      if (!Array.isArray(options)) return [];
      const shownOptions = this.pollData && Array.isArray(this.pollData.options) ? this.pollData.options : [];
      return options.map((opt) => {
        const shown = shownOptions.find((o) => o.optionID === opt.optionID);
        return {
          optionID: opt.optionID || "",
          optionText: opt.optionText || "",
          voteCount: opt.voteCount || 0,
          optionImage: (shown && shown.optionImage) || opt.optionImage || "",
        };
      });
    },

    // ══════════════════════════════════════════════════
    //  Participants
    // ══════════════════════════════════════════════════
    async fetchParticipants() {
      this.participantLoading = true;
      try {
        const colors = ["#dbeafe", "#dcfce7", "#fde68a", "#fae8ff", "#e2e8f0"];
        const list = [];

        // Add current user (local participant)
        if (this.room && this.room.localParticipant) {
          const localName = this.room.localParticipant.name || localStorage.getItem("userName") || "You";
          list.push({
            id: "local",
            initials: localName.split(" ").filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join("") || "?",
            name: localName + " (You)",
            role: "Learner",
            badge: "In Room",
            badgeColor: "#dcfce7",
            badgeText: "#166534",
            color: colors[0],
          });
        }

        // Add all remote participants currently connected via LiveKit
        if (this.room && this.room.remoteParticipants) {
          let i = 1;
          this.room.remoteParticipants.forEach((participant) => {
            const name = participant.name || participant.identity || `Participant ${i + 1}`;
            list.push({
              id: participant.sid || i,
              initials: name.split(" ").filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join("") || "?",
              name,
              role: participant.metadata ? (JSON.parse(participant.metadata).role || "Participant") : "Participant",
              badge: "In Room",
              badgeColor: "#dcfce7",
              badgeText: "#166534",
              color: colors[i % colors.length],
            });
            i++;
          });
        }

        this.participants = list;
      } catch (_) {
        console.warn('[LiveStreamingRoom] fetchParticipants:', _);
        this.participants = [];
      } finally {
        this.participantLoading = false;
      }
    },

    // ══════════════════════════════════════════════════
    //  Q&A
    // ══════════════════════════════════════════════════
    // B02: poll Q&A ทุก 4 วิ เฉพาะตอนเปิดแท็บ Q&A (D11) — เรียกจาก watch.selectedTab
    startQaPolling() {
      this.stopQaPolling();
      if (this.questions.length) this.scrollQaToBottom(); // เปิดแท็บ → รายการเดิมอยู่ล่างสุดทันที (D29)
      this.fetchQA();
      this.qaPollInterval = setInterval(() => this.fetchQA({ silent: true }), 4000);
    },

    stopQaPolling() {
      if (this.qaPollInterval) {
        clearInterval(this.qaPollInterval);
        this.qaPollInterval = null;
      }
    },

    // B02: map 1 รายการจาก API → การ์ด (ใช้ทั้ง fetchQA และ submitQuestion) — field/ข้อความ/สีเดิมทุกตัว (U2)
    mapQaItem(q, i) {
      const allowed = q.status === 1 || q.allowed === true;
      return {
        id: q.id || q.ID || i,
        question: q.question || q.Question || "",
        askedBy: q.askedBy || q.userName || q.senderName || "Participant",
        time: q.createdDate ? new Date(q.createdDate).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "",
        statusLabel: allowed ? "Allowed to speak" : "Pending",
        statusColor: allowed ? "#dcfce7" : "#fef9c3",
        statusText: allowed ? "#166534" : "#854d0e",
      };
    },

    // B02: เลื่อนรายการ Q&A ลงล่างสุด (แบบ scrollChatToBottom)
    scrollQaToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.qaList;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },

    // B02: ความสูงจริงของ footer ของ Navbar จาก DOM (ไม่ใช้ $vuetify.application.footer — = 0 เมื่อลงทะเบียนตอนซ่อน, U7) · ซ่อน/ไม่พบ = 0 · ceil กันทับ 1px
    measureFooter() {
      const el = document.querySelector('footer.v-footer.foot-class');
      this.footerHeight = el ? Math.ceil(el.getBoundingClientRect().height) : 0;
    },

    // B02: โหลด/poll คำถาม — ข้ามถ้ายังค้าง (D11) · ทิ้งผลเก่า (D16) · ล้มไม่ล้างรายการ (D28) · 401 หยุด poll (UC-17) · เลื่อนตาม D29
    async fetchQA({ silent = false } = {}) {
      const liveSessionID = this.$route.query.liveSessionID;
      if (!liveSessionID || this.qaFetching) return;
      this.qaFetching = true;
      const seq = ++this.qaSeq; // นับหลังเช็กข้าม — ห้ามให้รอบที่ถูกข้ามทำให้รอบที่ค้างกลายเป็นผลเก่า
      if (!silent && !this.qaLoadedOnce) this.qaLoading = true;
      try {
        const res = await this.$axios.get(`${this.web_url}LiveSession/GetSessionQA/${liveSessionID}`);
        if (seq !== this.qaSeq) return; // มี poll/การส่งที่ใหม่กว่า
        if (res.data?.status !== 0) throw new Error(res.data?.message || "GetSessionQA failed");
        const items = Array.isArray(res.data.data) ? res.data.data : [];
        const list = this.$refs.qaList; // วัดก่อนแทนค่า
        const nearBottom = !list || list.scrollHeight - list.scrollTop - list.clientHeight < 80;
        this.questions = items.filter((q) => q.status !== 2).map((q, i) => this.mapQaItem(q, i));
        this.qaLoadError = "";
        this.qaLoadedOnce = true;
        if (!silent || nearBottom) this.scrollQaToBottom();
      } catch (err) {
        if (seq !== this.qaSeq) return; // ผลเก่า (D16) — ไม่แตะ error
        console.warn('[LiveStreamingRoom] fetchQA:', err);
        if (err?.response?.status === 401) {
          this.stopQaPolling(); // ลองซ้ำไม่มีทางผ่าน
          this.qaLoadError = "Your session has expired. Please sign in again.";
        } else if (!this.qaLoadedOnce) {
          this.qaLoadError = "Couldn't load questions. Retrying…"; // poll ลองต่อ
        }
        // เคยโหลดแล้ว → เงียบ คงรายการเดิม
      } finally {
        this.qaFetching = false;
        this.qaLoading = false;
      }
    },

    // B02: ส่งคำถาม — กันซ้ำ (D25) · ไม่เงียบเมื่อไม่มี UserID (U11) · error ใต้ช่องและไม่ล้างข้อความ (AC-5) · เห็นทันที + เลื่อนล่างสุด (AC-1, D29) · คงส่ง userID (D22)
    async submitQuestion() {
      if (this.submittingQuestion) return;
      const text = this.newQuestion.trim();
      if (!text) return;
      const liveSessionID = this.$route.query.liveSessionID;
      const userID = Number.parseInt(localStorage.getItem("UserID"));
      if (!liveSessionID || !userID) {
        this.qaError = "Couldn't send your question. Please re-join the live session.";
        return;
      }
      this.qaError = "";
      this.submittingQuestion = true;
      try {
        const res = await this.$axios.post(`${this.web_url}LiveSession/AddSessionQA`, {
          liveSessionID,
          userID,
          question: text,
        });
        this.qaSeq++; // ทิ้งผล poll ที่ค้างอยู่ (D16)
        this.newQuestion = "";
        const created = res.data?.status === 0 ? res.data.data : null;
        if (created && !this.questions.some((q) => q.id === created.id)) {
          this.questions.push(this.mapQaItem(created, this.questions.length));
        }
        this.scrollQaToBottom();
        this.fetchQA({ silent: true });
      } catch (err) {
        console.warn('[LiveStreamingRoom] submitQuestion:', err);
        const status = err?.response?.status;
        const apiMessage = err?.response?.data?.message;
        if (status === 401) this.qaError = "Your session has expired. Please sign in again.";
        else if (status === 400 && apiMessage) this.qaError = apiMessage;
        else this.qaError = "Couldn't send your question. Please try again."; // 500 / เน็ต / ProblemDetails
      } finally {
        this.submittingQuestion = false;
      }
    },

    // ══════════════════════════════════════════════════
    //  Recordings
    // ══════════════════════════════════════════════════
    async fetchRecordings() {
      const liveSessionID = this.$route.query.liveSessionID;
      if (!liveSessionID) return;
      this.recordingsLoading = true;
      try {
        const res = await this.$axios.get(
          `${this.web_url}LiveSession/GetSessionRecordings/${liveSessionID}`
        );
        const rawRecItems = res.data?.data ?? res.data;
        const items = Array.isArray(rawRecItems) ? rawRecItems : [];
        this.recordings = items;
      } catch (_) {
        console.warn('[LiveStreamingRoom] fetchRecordings:', _);
        this.recordings = [];
      } finally {
        this.recordingsLoading = false;
      }
    },

    formatRecordingDate(value) {
      if (!value) return "";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    },
  },
};
</script>

<style scoped>
/* ── Full-page wrapper ────────────────────────────────── */
.room-wrapper {
  display: flex;
  height: calc(100vh - 64px);
  background: #1a1a1a;
  overflow: hidden;
  position: relative;
}

/* ── Connecting overlay ───────────────────────────────── */
.connecting-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #1a1a1a;
  z-index: 100;
}

/* ── Main stage ───────────────────────────────────────── */
.main-stage {
  position: relative;
  flex: 1;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.main-video {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.draw-overlay {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 5;
}

.no-video-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* ── Screen Share Layout (screen share + camera filmstrip) ── */
.screen-share-layout {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
}

.screen-share-main {
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
}

.camera-filmstrip {
  display: flex;
  gap: 8px;
  padding: 8px 12px;
  background: #1a1a1a;
  overflow-x: auto;
  flex-shrink: 0;
  min-height: 100px;
  align-items: center;
}

.filmstrip-item {
  position: relative;
  width: 140px;
  min-width: 140px;
  height: 84px;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid rgba(255, 255, 255, 0.15);
  background: #222;
  flex-shrink: 0;
}

.filmstrip-item--local {
  border-color: #a6cc39;
}

.filmstrip-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.filmstrip-label {
  position: absolute;
  bottom: 2px;
  left: 4px;
  font-size: 10px;
  color: #fff;
  background: rgba(0, 0, 0, 0.55);
  padding: 1px 5px;
  border-radius: 4px;
}

/* ── Camera Grid Layout (no screen share) ─────────────── */
.camera-grid-layout {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  grid-auto-rows: 1fr;
  gap: 4px;
  width: 100%;
  height: 100%;
  padding: 4px;
  position: relative;
}

.grid-video-item {
  position: relative;
  background: #222;
  border-radius: 8px;
  overflow: hidden;
  min-height: 180px;
}

.grid-video-item--single {
  grid-column: 1 / -1;
  grid-row: 1 / -1;
}

.grid-video-item--local {
  border: 2px solid #a6cc39;
}

.grid-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.grid-video-label {
  position: absolute;
  bottom: 6px;
  left: 8px;
  font-size: 12px;
  color: #fff;
  background: rgba(0, 0, 0, 0.55);
  padding: 2px 8px;
  border-radius: 4px;
}

.local-mirror {
  transform: scaleX(-1);
}

/* ── Controls bar ─────────────────────────────────────── */
.controls-bar {
  position: absolute;
  top: 14px;
  right: 14px;
  display: flex;
  align-items: center;
  z-index: 20;
}

/* ── Viewer media controls bar ──────────────────────── */
.media-controls-bar {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.45);
  border-radius: 40px;
  padding: 6px 12px;
  z-index: 20;
  backdrop-filter: blur(6px);
}

/* ── Media disabled notice ───────────────────────────── */
.media-disabled-notice {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.45);
  border-radius: 20px;
  padding: 5px 14px;
  z-index: 20;
  backdrop-filter: blur(6px);
  color: rgba(255, 255, 255, 0.65);
  font-size: 12px;
  white-space: nowrap;
}

/* ── Breakout banner ─────────────────────────────────── */
.breakout-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 300;
  background: #A6CC39;
  color: #1a2e05;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 16px;
  letter-spacing: 0.3px;
}

/* ── Chat sidebar ─────────────────────────────────────── */
.chat-sidebar {
  width: 300px;
  min-width: 300px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  border-left: 1px solid #e0e0e0;
  position: relative;
}

.chat-header {
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
}

/* ── Sidebar Tab Bar ──────────────────────────────────── */
.sidebar-tab-bar {
  display: flex;
  border-bottom: 1px solid #e0e0e0;
  overflow-x: auto;
  flex-wrap: nowrap;
}

.sidebar-tab-btn {
  flex: none;
  min-width: 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 8px;
  border: none;
  background: none;
  font-size: 12px;
  font-weight: 500;
  color: #9e9e9e;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.2s ease;
}

.sidebar-tab-btn.active {
  color: #5a7a00;
  border-bottom-color: #5a7a00;
}

.sidebar-tab-btn:hover:not(.active) {
  color: #616161;
  background: #fafafa;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
}

/* ── Chat Row Layout (matches host chat) ───────────── */
.chat-row {
  align-items: flex-end;
  display: flex;
  gap: 8px;
}

.chat-row--mine {
  flex-direction: row-reverse;
}

.chat-row--theirs {
  flex-direction: row;
}

/* ── Body ──────────────────────────────────────────── */
.chat-row__body {
  display: flex;
  flex-direction: column;
  flex: 0 1 auto;
  gap: 2px;
  max-width: 72%;
  min-width: 0;
}

.chat-row--mine .chat-row__body {
  align-items: flex-end;
}

.chat-row--theirs .chat-row__body {
  align-items: flex-start;
}

/* ── Sender name ───────────────────────────────────── */
.chat-row__sender {
  color: #475569;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  margin-bottom: 1px;
  padding-left: 2px;
}

/* ── Bubble group ──────────────────────────────────── */
.chat-row__group {
  align-items: flex-end;
  display: flex;
  gap: 4px;
}

.chat-row--mine .chat-row__group {
  flex-direction: row-reverse;
}

/* ── Bubble ────────────────────────────────────────── */
.chat-row__bubble {
  font-size: 0.875rem;
  line-height: 1.55;
  padding: 9px 13px;
  word-break: break-word;
}

.chat-row__bubble--mine {
  background: #a6cc39;
  border-radius: 16px 16px 4px 16px;
  box-shadow: 0 2px 8px rgba(166, 204, 57, 0.22);
  color: #424242;
}

.chat-row__bubble--theirs {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px 16px 16px 4px;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.06);
  color: #1e293b;
}

/* ── Timestamp foot ────────────────────────────────── */
.chat-row__foot {
  align-items: center;
  display: flex;
  gap: 3px;
  padding: 0 2px;
}

.chat-row--mine .chat-row__foot {
  justify-content: flex-end;
}

.chat-row--theirs .chat-row__foot {
  justify-content: flex-start;
}

.chat-row__ts {
  color: #94a3b8;
  font-size: 0.68rem;
  letter-spacing: 0.02em;
}

/* ── Reply quote block inside bubble ───────────────── */
.chat-quote {
  border-left: 3px solid #6CC8A0;
  border-radius: 6px 6px 0 0;
  background: rgba(108, 200, 160, 0.1);
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-bottom: 4px;
  padding: 4px 8px;
}

.chat-quote--mine {
  background: rgba(255, 255, 255, 0.18);
  border-left-color: rgba(255, 255, 255, 0.7);
}

.chat-quote-name {
  color: #6CC8A0;
  font-size: 11px;
  font-weight: 700;
}

.chat-quote--mine .chat-quote-name {
  color: rgba(255, 255, 255, 0.9);
}

.chat-quote-text {
  color: #475569;
  font-size: 12px;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chat-quote--mine .chat-quote-text {
  color: rgba(255, 255, 255, 0.7);
}

/* ── Per-bubble reply button ───────────────────────── */
.chat-reply-btn {
  font-size: 0.72rem !important;
  min-width: 0 !important;
  opacity: 0;
  padding: 0 4px !important;
  transition: opacity 0.18s ease;
}

.chat-bubble-wrap:hover .chat-reply-btn {
  opacity: 1;
}

/* ── Reply compose banner ──────────────────────────── */
.chat-reply-banner {
  align-items: center;
  background: #f0faf6;
  border-left: 3px solid #6CC8A0;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
  padding: 6px 10px;
}

.chat-reply-banner__content {
  align-items: center;
  display: flex;
  flex: 1;
  gap: 4px;
  min-width: 0;
  overflow: hidden;
}

.chat-reply-banner__name {
  color: #2d9e72;
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
}

/* ── Compose area + pill input ─────────────────────── */
.chat-compose {
  flex: 0 0 auto;
  padding: 8px 12px;
}

.chat-input-pill {
  border-radius: 30px !important;
}

.chat-input-pill>>>.v-input__control,
.chat-input-pill>>>fieldset {
  border-radius: 9999px !important;
}

.chat-input-pill>>>.v-input__slot {
  align-items: center !important;
  background: #ffffff !important;
  border: 1px solid #cbd5e1 !important;
  border-radius: 9999px !important;
  box-shadow: none !important;
  min-height: 48px !important;
  padding-left: 20px !important;
  padding-right: 6px !important;
}

.chat-input-pill>>>input {
  font-size: 0.9rem;
}

.chat-input-pill>>>.v-input__append-inner {
  align-self: center;
  margin-top: 0 !important;
}

.chat-input-pill>>>.v-input__icon--append .v-icon {
  background-color: #166534;
  border-radius: 50%;
  color: #ffffff !important;
  cursor: pointer;
  font-size: 16px !important;
  height: 32px !important;
  line-height: 32px;
  text-align: center;
  width: 32px !important;
}

/* ── Responsive: collapse chat on small screens ──────── */
@media (max-width: 768px) {
  .chat-sidebar {
    display: none;
  }
}

/* ── Emoji Reaction Bar ───────────────────────────────── */
.emoji-bar {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  gap: 8px;
  background-color: #faf8f5;
  border-top: 1px solid #e0e0e0;
}

.emoji-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background-color: #f5f5f5;
  font-size: 22px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease, background-color 0.15s ease;
}

.emoji-btn:hover {
  background-color: #ebebeb;
  transform: scale(1.15);
}

.emoji-btn:active {
  transform: scale(0.9);
}

/* ── Floating Emoji Animation ─────────────────────────── */
.floating-emojis-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 100;
}

.floating-emoji {
  position: absolute;
  bottom: 35%;
  font-size: 28px;
  animation: floatUp 1.5s ease-out forwards;
  pointer-events: none;
}

@keyframes floatUp {
  0% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }

  100% {
    opacity: 0;
    transform: translateY(-150px) scale(1.3);
  }
}

/* ── Poll Tab ─────────────────────────────────────────── */
.poll-tab {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 12px;
}

.poll-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
}

.poll-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: #9e9e9e;
}

.poll-empty-icon {
  font-size: 40px;
  margin-bottom: 8px;
  opacity: 0.5;
}

.poll-empty p {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.38);
}

/* Active Poll Card */
.poll-card {
  background: #ffffff;
  border-radius: 10px;
  border: 1.5px solid #6cc8a0;
  padding: 14px;
}

.poll-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
}

.poll-header-icon {
  font-size: 18px;
}

.poll-header-text {
  color: #6cc8a0;
  font-size: 12px;
  font-weight: 500;
}

.poll-question {
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 4px;
}

.poll-multi-hint {
  color: rgba(0, 0, 0, 0.45);
  font-size: 11px;
  margin-bottom: 8px;
}

/* Options Grid */
.poll-options-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin: 14px 0 16px;
}

.poll-option-btn {
  padding: 14px 12px;
  border-radius: 6px;
  border: 1px solid #bdbdbd;
  background-color: #f5f5f5;
  font-size: 13px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
  text-align: center;
  transition: all 0.15s ease;
}

.poll-option-btn:hover:not(.disabled) {
  border-color: #6cc8a0;
  background-color: #f0faf5;
}

.poll-option-btn.selected {
  background-color: #e8f5e9;
  border-color: #6cc8a0;
  border-width: 2px;
  color: #2e7d32;
  font-weight: 600;
}

.poll-option-btn.disabled {
  color: rgba(0, 0, 0, 0.38);
  cursor: not-allowed;
}

/* B01: option with image — whole image visible (contain), fixed 72px frame */
.poll-option-btn--with-image {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
  padding: 8px;
}

.poll-option-img {
  display: block;
  width: 100%;
  height: 72px;
  object-fit: contain;
  border-radius: 4px;
  background: #ffffff;
  pointer-events: none;
}

.poll-option-text {
  display: block;
  overflow-wrap: anywhere;
}

.poll-option-btn.disabled .poll-option-img {
  opacity: 0.5;
}

/* Vote Button */
.vote-submit-btn {
  width: 100%;
  padding: 12px;
  border-radius: 8px;
  border: none;
  background-color: #6cc8a0;
  color: #14532d;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.vote-submit-btn:hover:not(:disabled) {
  background-color: #5bb890;
}

.vote-submit-btn:disabled {
  background-color: #bdbdbd;
  cursor: not-allowed;
}

.btn-spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Vote Confirmation */
.poll-voted-confirmation {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
}

.voted-card {
  background: white;
  border-radius: 10px;
  border: 1.5px solid #6cc8a0;
  padding: 20px;
  text-align: center;
}

.voted-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: #6cc8a0;
  color: #14532d;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
}

.voted-card h3 {
  color: rgba(0, 0, 0, 0.87);
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 6px;
}

.voted-question {
  color: rgba(0, 0, 0, 0.54);
  font-size: 13px;
}

/* Timeout State */
.poll-timeout {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
}

.timeout-card {
  background: white;
  border-radius: 10px;
  border: 1.5px solid #e57373;
  padding: 20px;
  text-align: center;
}

.timeout-icon {
  font-size: 48px;
  margin-bottom: 12px;
}

.timeout-card h3 {
  color: rgba(0, 0, 0, 0.87);
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 6px;
}

.timeout-card p {
  color: rgba(0, 0, 0, 0.54);
  font-size: 13px;
}

.timeout-question {
  color: rgba(0, 0, 0, 0.38);
  font-size: 12px;
  margin-top: 4px;
}

/* Results View */
.poll-results {
  padding: 0;
}

.poll-results .poll-question {
  margin-bottom: 12px;
}

.results-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.result-item {
  display: flex;
  flex-direction: column;
}

.result-bar-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.result-bar-bg {
  flex: 1;
  position: relative;
  height: 32px;
  background-color: #e8e8e8;
  border-radius: 4px;
  overflow: hidden;
}

.result-bar-fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: linear-gradient(to right, #6cc8a0, #a8d98a);
  border-radius: 4px;
  transition: width 0.5s ease;
}

.result-option-text {
  position: absolute;
  top: 0;
  left: 10px;
  height: 100%;
  display: flex;
  align-items: center;
  color: rgba(0, 0, 0, 0.87);
  font-size: 12px;
  font-weight: 500;
  z-index: 1;
}

.result-percentage {
  color: rgba(0, 0, 0, 0.54);
  font-size: 12px;
  white-space: nowrap;
  min-width: 32px;
  text-align: right;
}

/* B01: 32×32 thumb slot in results (cover) */
.result-thumb-slot {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 4px;
  background: #f5f5f5;
  overflow: hidden;
}

.result-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* ── Extra tab panels (People / Q&A / Recordings) ────── */
.extra-tab {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.extra-tab--qa {
  overflow: hidden;
}

.extra-tab__list {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
}

.extra-tab__empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #9e9e9e;
  font-size: 13px;
  padding: 24px;
  text-align: center;
}

/* Participant row */
.participant-row {
  display: flex;
  align-items: center;
  background: #f8fafc;
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 12px;
  padding: 10px;
  margin-bottom: 8px;
}

.participant-initials {
  font-size: 0.8rem;
  font-weight: 700;
  color: #0f172a;
}

.participant-info {
  flex: 1;
  min-width: 0;
}

.participant-name {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.participant-role {
  font-size: 11px;
  color: #64748b;
}

/* Q&A item */
.qa-item {
  background: #f8fafc;
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 12px;
  padding: 10px 12px;
  margin-bottom: 10px;
}

.qa-item__question {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 4px;
  overflow-wrap: anywhere; /* B02 D19: ข้อความยาวไม่มีช่องว่างไม่ล้นแผง */
  white-space: pre-wrap;   /* B02 D19: แสดงขึ้นบรรทัดตาม \n */
}

.qa-item__meta {
  font-size: 11px;
  color: #64748b;
}

/* B02: เหลือช่อง pill (.chat-input-pill เดิม) + บรรทัด error — ไม่ใช้ flex แล้ว */
.qa-compose {
  padding: 8px 12px;
  border-top: 1px solid #e0e0e0;
  flex-shrink: 0;
}

/* B02: ข้อความ error (#E53935 = สีปุ่ม Leave/ไมค์ปิดในไฟล์) */
.qa-compose__error,
.qa-list__error {
  color: #E53935;
  font-size: 12px;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.qa-compose__error {
  margin-top: 6px;
  padding: 0 12px;
}

.qa-list__error {
  margin-bottom: 8px;
}

/* Recording row */
.recording-row {
  background: #f8fafc;
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 12px;
  padding: 10px 12px;
  margin-bottom: 10px;
}

.recording-row__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.recording-row__title {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
}

.recording-row__date {
  font-size: 11px;
  color: #64748b;
}
</style>
